import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getStoredRefCode, getStoredRefSessionId, clearStoredRefCode } from '@/components/RefTracker';
// Импорт ради побочного эффекта тоже: модуль ловит utm-метки на импорте,
// до первого рендера — как и ранний захват ?ref= в RefTracker.
import { getStoredAttribution } from '@/lib/attribution';
import { clearBirthDataStorage } from '@/lib/birthDataStorage';
import { API_URL as API_BASE, BACKEND_URL } from '@/lib/backendUrl';

const AuthContext = createContext(null);

// Lightweight fetch wrapper (replaces axios — saves ~30KB from bundle).
// Экспортируется, чтобы им могли пользоваться и компоненты вне этого модуля:
// один axios, затянутый ради пары GET-ов, стоит бандлу больше, чем весь этот файл.
// Пояс аккаунта — единый для страницы дня, ленты, карточки главной и письма.
// У аккаунта его нет — один раз отдаём серверу пояс браузера (сервер запишет,
// только если в аккаунте пусто). Дальше пояс берётся из аккаунта, не из браузера.
function saveBrowserTimezoneOnce(userData, token) {
  if (!userData || userData.timezone || userData.gmt) return;
  let tz = '';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { tz = ''; }
  if (!tz) return;
  try {
    if (sessionStorage.getItem('tz_saved') === tz) return;
    sessionStorage.setItem('tz_saved', tz);
  } catch { /* приватный режим — просто отправим */ }
  api('POST', '/auth/timezone', { timezone: tz }, token).catch(() => {});
}

export async function api(method, path, data, token) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (token) opts.headers['Authorization'] = `Bearer ${token}`;
  if (data) opts.body = JSON.stringify(data);
  
  const res = await fetch(`${API_BASE}${path}`, opts);
  const json = await res.json();
  if (!res.ok) {
    const err = new Error(json.detail || 'Request failed');
    err.response = { data: json, status: res.status };
    throw err;
  }
  return json;
}

// Запись в localStorage может не состояться: приватный режим, переполненное
// хранилище, запрет на доступ к данным сайта. Ни то, ни другое не должно
// ломать вход и регистрацию — токен и пользователь уже лежат в контексте
// авторизации, вкладка работает как обычно. Пишем в консоль и идём дальше.
export function safeStore(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`Не удалось сохранить "${key}" в localStorage:`, e);
    return false;
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Продление сессии при активности: если токену больше суток, тихо просим
// у бэкенда свежий. Пока человек заходит хотя бы раз в неделю, сессия живёт.
const TOKEN_REFRESH_AFTER_HOURS = 24;

function tokenAgeHours(token) {
  try {
    const raw = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(raw));
    if (!payload || !payload.iat) return null;
    return (Date.now() / 1000 - payload.iat) / 3600;
  } catch (e) {
    return null;
  }
}

async function refreshTokenIfStale(token) {
  const age = tokenAgeHours(token);
  if (age === null || age < TOKEN_REFRESH_AFTER_HOURS) return;
  try {
    const res = await api('POST', '/auth/refresh', {}, token);
    if (res && res.access_token) safeStore('auth_token', res.access_token);
  } catch (e) {
    // Продление не критично: текущий токен ещё жив, попробуем в следующий раз.
  }
}

// Токен админа на время «Войти как» (пишет UsersSection, читает returnToAdmin).
export const ADMIN_ORIGINAL_TOKEN_KEY = 'admin_original_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      let data = null;
      let lastError = null;
      // Временная недоступность бэкенда (деплой, сеть, 5xx) НЕ должна
      // разлогинивать: пробуем несколько раз, а токен стираем только если
      // сервер прямо ответил, что он невалиден (401/403).
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          data = await api('GET', '/auth/me', null, token);
          lastError = null;
          break;
        } catch (error) {
          lastError = error;
          const status = error && error.response ? error.response.status : null;
          if (status === 401 || status === 403) break;
          await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
        }
      }

      if (data) {
        setUser(data);
        setIsAuthenticated(true);
        safeStore('user', JSON.stringify(data));
        refreshTokenIfStale(token);
        saveBrowserTimezoneOnce(data, token);
      } else {
        const status = lastError && lastError.response ? lastError.response.status : null;
        if (status === 401 || status === 403) {
          console.error('Failed to load user:', lastError);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setUser(null);
          setIsAuthenticated(false);
        } else {
          // Бэкенд временно недоступен — сессию не рвём, показываем кэш.
          console.warn('auth/me unavailable, session kept:', lastError);
          const cached = localStorage.getItem('user');
          if (cached) {
            try {
              setUser(JSON.parse(cached));
              setIsAuthenticated(true);
            } catch (e) {
              setIsAuthenticated(false);
            }
          }
        }
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const data = await api('POST', '/auth/login', { 
      email, password, timezone: browserTimezone
    });

    if (data.requires_2fa) {
      return { requires_2fa: true, pending_token: data.pending_token, message: data.message };
    }

    const { access_token, user: userData } = data;
    completeLogin(access_token, userData);
    return { requires_2fa: false };
  };

  const verify2FA = async (pendingToken, code) => {
    const data = await api('POST', '/admin/verify-2fa', {
      pending_token: pendingToken, code
    });
    const { access_token, user: userData } = data;
    completeLogin(access_token, userData);
  };

  const completeLogin = (access_token, userData) => {
    // Критерий успешного входа — ответ сервера с токеном, а не удавшаяся
    // запись в браузер: сначала контекст, потом localStorage.
    setUser(userData);
    setIsAuthenticated(true);
    safeStore('auth_token', access_token);
    safeStore('user', JSON.stringify(userData));

    import('@/lib/analytics').then(({ trackEvent, identifyUser }) => {
      trackEvent('login_completed', { method: 'email' });
      if (userData?.id) identifyUser(userData.id, { user_tier: userData.effective_tier_name || '' });
    });
    
    // Fetch full user data
    api('GET', '/auth/me', null, access_token).then(fullUserData => {
      setUser(fullUserData);
      safeStore('user', JSON.stringify(fullUserData));
    }).catch(() => {});
  };

  const register = async (email, password, options = {}) => {
    const refCode = getStoredRefCode();
    const refSessionId = getStoredRefSessionId();
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // `email_marketing_consent` defaults to true on the register form
    // (pre-checked). Pass through whatever the user submitted so the
    // backend can store the exact decision.
    const emailMarketingConsent =
      options.emailMarketingConsent === undefined ? true : !!options.emailMarketingConsent;
    // Галочка «Получать акции и предложения» (14.10.2026) — пустая по
    // умолчанию. Передана — новое окно: прогнозы идут как часть услуги,
    // реклама — только при true. Не передана — старый вызов, как было.
    const promoConsent = options.promoConsent === undefined ? undefined : !!options.promoConsent;
    // Источник первого визита: utm-метки, реферер и страница входа.
    // Лежат в localStorage с первого захода — отдаём как есть.
    const attribution = getStoredAttribution();
    const firstName = (options.firstName || '').trim();

    const data = await api('POST', '/auth/register', {
      email,
      // Пароля может не быть вовсе: окно натальной карты регистрирует одной
      // строкой. Сервер в этом случае заводит аккаунт без пароля — вход
      // потом по ссылке из письма.
      ...(password ? { password } : {}),
      // Поле-ловушка окна: у человека оно скрыто и всегда пустое.
      ...(options.honeypot ? { website: options.honeypot } : {}),
      ref_code: refCode,
      timezone: browserTimezone,
      referral_session_id: refSessionId,
      email_marketing_consent: emailMarketingConsent,
      ...(promoConsent === undefined ? {} : { promo_consent: promoConsent }),
      ...(firstName ? { first_name: firstName } : {}),
      ...attribution,
    });
    const { access_token, user: userData } = data;

    // Аккаунт создан — это решил сервер. Цель уходит сразу по ответу, ДО
    // любой работы с localStorage: сбой записи в браузер не должен отнимать
    // у владельца засчитанную регистрацию.
    const { trackEvent, identifyUser } = await import('@/lib/analytics');
    // place передаёт вызывающий экран (окно карты — modal_inline). Не передан
    // — параметров у события ровно столько же, сколько было: страница
    // регистрации свою цель не меняет.
    trackEvent('signup_completed', {
      method: 'email',
      ...(options.place ? { place: options.place } : {}),
    });
    if (userData?.id) identifyUser(userData.id, { user_tier: userData.effective_tier_name || 'TRIAL' });
    import('@/lib/topMailRu').then(({ trackGoal }) => {
      trackGoal('completeRegistration');
    });

    // Человек авторизован в этой вкладке независимо от того, дошла ли
    // запись до хранилища.
    setUser(userData);
    setIsAuthenticated(true);
    try { clearStoredRefCode(); } catch (e) { console.warn('Не удалось очистить реферальный код:', e); }
    safeStore('auth_token', access_token);
    safeStore('user', JSON.stringify(userData));

    try {
      const fullUserData = await api('GET', '/auth/me', null, access_token);
      setUser(fullUserData);
      safeStore('user', JSON.stringify(fullUserData));
      return fullUserData;
    } catch (err) {
      console.error('Failed to fetch full user data:', err);
      return userData;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // Токен админа, сохранённый при «Войти как», уходит вместе с выходом:
    // иначе следующий, кто войдёт в этом браузере, увидел бы «Вернуться в
    // админку» и попал бы в чужую админскую сессию.
    localStorage.removeItem(ADMIN_ORIGINAL_TOKEN_KEY);
    // Данные рождения и формы программ — только этого человека: после выхода
    // они не должны подставляться гостю (14.10.2026).
    clearBirthDataStorage();
    setUser(null);
    setIsAuthenticated(false);
  };

  // «Войти как» → обратно в админку без пароля. Токен админа сохранён в этом
  // же браузере при входе как пользователь (UsersSection, admin_original_token)
  // — это та же сессия, что была до «Войти как», новой не выдаём. Перед
  // возвратом сервер подтверждает, что токен жив и это админ; нет — ключ
  // стираем и отправляем на обычный вход.
  const returnToAdmin = async () => {
    const adminToken = localStorage.getItem(ADMIN_ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(ADMIN_ORIGINAL_TOKEN_KEY);
    let me = null;
    if (adminToken) {
      try {
        me = await api('GET', '/auth/me', null, adminToken);
      } catch (_e) {
        me = null;
      }
    }
    clearBirthDataStorage();
    if (!me || me.role !== 'admin') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/auth/login?redirect=%2Fadmin';
      return false;
    }
    safeStore('auth_token', adminToken);
    safeStore('user', JSON.stringify(me));
    window.location.href = '/admin';
    return true;
  };

  const hasRole = (requiredRole) => {
    if (!user) return false;
    const roleHierarchy = { guest: 0, user: 1, manager: 2, admin: 3 };
    return roleHierarchy[user.role] >= roleHierarchy[requiredRole];
  };

  const isAdmin = () => user?.role === 'admin';
  const isManager = () => user?.role === 'manager' || user?.role === 'admin';

  return (
    <AuthContext.Provider
      value={{
        user, loading, isAuthenticated,
        login, verify2FA, register, logout, returnToAdmin,
        hasRole, isAdmin, isManager,
        // Expose the user-reloader under both names: `refreshUser` (current convention)
        // and `loadUser` (verbatim match of the inner function, used by a few newer pages).
        refreshUser: loadUser,
        loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
